import type { Grader } from '../../../hooks/useGraders/useGraders.interface.js';
import type { VariableDeclaration } from '../../../types/agent.js';
import type { UpsertEvaluationTemplateBody } from '../../../types/evaluationTemplate.js';
import { defaultBindingFor } from '../../EvaluationTemplatesPage/EvaluationTemplateForm/bindings.utils.js';
import {
  QUERY_SET_NAME,
  parseInlineItems,
} from '../../EvaluationTemplatesPage/EvaluationTemplateForm/EvaluationTemplateForm.utils.js';

const VARIABLE_REFERENCE = /\{\{\$var:([A-Z][A-Z0-9_]*)\}\}/g;
const EXPRESSION =
  /^\{\{\$expr:\s*\$\.queryItem\.data\.([A-Za-z_$][\w$]*)\s*\}\}$/;
const TURN_OUTPUT_EXPRESSION = /^\{\{\$expr:\s*\$\.turn\.output\s*\}\}$/;
const VARIABLE_NAME = /^[A-Z][A-Z0-9_]*$/;

function typedLiteral(
  name: string,
  value: string,
  type: VariableDeclaration['type'],
): string | number | boolean {
  if (type === 'Boolean') {
    if (/^true$/i.test(value)) return true;
    if (/^false$/i.test(value)) return false;
    throw new TypeError(`${name} must be true or false.`);
  }
  if (type === 'Number' || type === 'Integer') {
    const number = Number(value);
    if (
      !value ||
      !Number.isFinite(number) ||
      (type === 'Integer' && !Number.isSafeInteger(number))
    ) {
      throw new TypeError(
        `${name} must be a ${type === 'Integer' ? 'safe integer' : 'finite number'}.`,
      );
    }
    return number;
  }
  return value;
}

export function referencedVariables(spec: unknown): string[] {
  const found = new Set<string>();
  const visit = (value: unknown) => {
    if (typeof value === 'string') {
      for (const match of value.matchAll(VARIABLE_REFERENCE))
        found.add(match[1]!);
    } else if (Array.isArray(value)) {
      value.forEach(visit);
    } else if (value !== null && typeof value === 'object') {
      Object.values(value).forEach(visit);
    }
  };
  visit(spec);
  return [...found].sort();
}

export function evaluationInputs(
  grader: Grader,
  agent?: { spec?: unknown; variables?: Record<string, VariableDeclaration> },
): Record<string, string> {
  const graderNames = new Set([
    ...referencedVariables(grader.rawSpec),
    ...Object.keys(grader.variables ?? {}),
  ]);
  const names = new Set([
    ...graderNames,
    ...referencedVariables(agent?.spec),
    ...Object.keys(agent?.variables ?? {}),
  ]);
  return Object.fromEntries(
    [...names].sort().map((name) => {
      const declared = grader.variables?.[name] ?? agent?.variables?.[name];
      return [
        name,
        declared?.default == null
          ? name === 'OPENAI_ENDPOINT' || name === 'GRADER_MODEL'
            ? ''
            : name === 'RESPONSE' && graderNames.has(name)
              ? '{{$expr:$.turn.output}}'
              : defaultBindingFor(name)
          : typeof declared.default === 'string'
            ? declared.default
            : (JSON.stringify(declared.default) ?? ''),
      ];
    }),
  );
}

export function validateTrialInputs(
  queryText: string,
  inputs: Record<string, string>,
  declarations: Record<string, VariableDeclaration> = {},
): string | null {
  const items = parseInlineItems(queryText);
  if (!items?.length)
    return 'Provide at least one valid query row (JSON array or JSONL).';
  let rows: unknown[];
  try {
    rows = items.map((item) => JSON.parse(item) as unknown);
  } catch {
    return 'Query rows must be JSON objects, not raw text.';
  }
  if (
    rows.some(
      (row) => row === null || typeof row !== 'object' || Array.isArray(row),
    )
  ) {
    return 'Every query row must be a JSON object.';
  }
  for (const [name, value] of Object.entries(inputs)) {
    if (!VARIABLE_NAME.test(name)) return `Invalid variable name: ${name}.`;
    if (!value.trim())
      return `Provide a value or query-row binding for ${name}.`;
    if (value.includes('{{$')) {
      if (TURN_OUTPUT_EXPRESSION.test(value.trim())) continue;
      const field = EXPRESSION.exec(value.trim())?.[1];
      if (!field)
        return `Use a query-row binding like {{$expr:$.queryItem.data.field}} or {{$expr:$.turn.output}} for ${name}.`;
      if (rows.some((row) => !(field in (row as Record<string, unknown>)))) {
        return `Query-row field ${field} is missing for ${name}.`;
      }
    } else if (declarations[name]) {
      try {
        typedLiteral(name, value.trim(), declarations[name].type);
      } catch (error) {
        if (!(error instanceof TypeError)) throw error;
        return error.message;
      }
    }
  }
  return null;
}

export function buildTypeScriptEvaluation(
  grader: Grader,
  agent: {
    name: string;
    version: number;
    variables?: Record<string, VariableDeclaration>;
  },
  queryText: string,
  inputs: Record<string, string>,
): UpsertEvaluationTemplateBody {
  const variables: Record<string, { type: string; default?: unknown }> = {};
  const agentBindings: Record<string, string> = {};
  const graderBindings: Record<string, string> = {};
  for (const [name, input] of Object.entries(inputs)) {
    const declaration = grader.variables?.[name] ?? agent.variables?.[name];
    const type = declaration?.type?.toLowerCase() ?? 'string';
    const value = input.trim();
    if (EXPRESSION.test(value) || TURN_OUTPUT_EXPRESSION.test(value)) {
      variables[name] = { type };
      graderBindings[name] = value;
      if (!TURN_OUTPUT_EXPRESSION.test(value)) agentBindings[name] = value;
    } else {
      variables[name] = {
        type,
        default: declaration
          ? typedLiteral(name, value, declaration.type)
          : value,
      };
    }
  }
  return {
    displayName: `Evaluate grader ${grader.name}`,
    querySets: [{ name: QUERY_SET_NAME, items: parseInlineItems(queryText)! }],
    tasks: [
      {
        name: 'default',
        querySet: QUERY_SET_NAME,
        agent: {
          name: agent.name,
          version: agent.version,
          with: agentBindings,
        },
        graders: [{ name: grader.name, with: graderBindings }],
      },
    ],
    variables,
    concurrency: { parallelism: 'task' },
  } as UpsertEvaluationTemplateBody;
}
